import React, { useEffect } from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from 'react-modal';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { taskApi } from '../../../apis/task/taskApi';
import { roomlabelApi } from '../../../apis/roomlabel/roomlabelApi';
import { getLabelColor } from '../../../libs/utils/getLabelColor';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { Task } from '../../../types/types';
import useLoading from '../../../hooks/useLoading';
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateTaskRoomLabelModalProps {
  onSubmitted: () => Promise<void>;
  task: Task;
}

interface LabelList {
  id: number;
  name: string;
  color: number;
}

export const UpdateTaskRoomLabelModal = (
  props: UpdateTaskRoomLabelModalProps
) => {
  const withLoading = useLoading();
  const { task, onSubmitted } = props;
  const { success } = useSystemMessage();
  const location = useLocation();
  const { goto } = useAppRouter();
  const [labelLists, setLabelLists] = React.useState<LabelList[]>([]);
  const [taskLabels, setTaskLabels] = React.useState<number[]>([]);
  const [disabled, setDisabled] = React.useState<boolean>(false);

  useEffect(() => {
    const fetch = async () => {
      withLoading(async () => {
        try {
          const response = await roomlabelApi.index({});
          setLabelLists(response.labelList);
        } catch(e) {
          await logError(
            location.pathname,
            'ラベル一覧の取得に失敗しました。',
            e
          );

          goto('internalServerError');
        }
      });
    };
    fetch();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [task]);

  useEffect(() => {
    setTaskLabels(task.taskLabelList.map((ll) => ll.id));
  }, [task, labelLists]);

  const handleChange = (value: string) => {
    if (taskLabels.includes(Number(value))) {
      setTaskLabels(taskLabels.filter((l) => l !== Number(value)));
    } else {
      setTaskLabels(taskLabels.concat(Number(value)));
    }
  };

  const handleSubmit = async () => {
    setDisabled(true);
    try {
      await taskApi.update(task.id, { name: task.name, taskLabels }, {});
      setDisabled(false);
      await onSubmitted();
      await success('ラベルを保存しました。');
    } catch(e) {
      await logError(
        location.pathname,
        'ラベルの保存に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">ラベル指定</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <div className="px-4 pt-8">
            <div className="px-2 grid grid-cols-2 gap-4">
              {taskLabels.length === 0 ? (
                <label htmlFor="task-label" className="btn-radio-pill-checked">
                  ラベル指定なし
                  <input
                    id="task-label"
                    type="checkbox"
                    className="hidden"
                    checked
                    onChange={() => setTaskLabels([])}
                  />
                </label>
              ) : (
                <label htmlFor="task-label" className="btn-radio-pill">
                  ラベル指定なし
                  <input
                    id="task-label"
                    type="checkbox"
                    className="hidden"
                    checked={false}
                    onChange={() => setTaskLabels([])}
                  />
                </label>
              )}
            </div>
            <div className="border border-primary my-8 border-opacity-50" />
            <div className="px-2 grid grid-cols-2 gap-4">
              {labelLists.map((ll) =>
                taskLabels.includes(ll.id) ? (
                  <label
                    key={ll.id}
                    htmlFor={`task-label${ll.id}`}
                    className="flex"
                  >
                    <div
                      data-tip={ll.name}
                      data-for={`label-${ll.id}`}
                      className={`btn-checkbox-rounded-checked ${getLabelColor(
                        ll.color
                      )}`}
                    >
                      <div className="w-32 truncate">{ll.name}</div>
                    </div>
                    <ReactTooltip
                      place="top"
                      type="dark"
                      effect="solid"
                      id={`label-${ll.id}`}
                      className="max-w-xs break-all text-xs"
                    />
                    <input
                      id={`task-label${ll.id}`}
                      type="checkbox"
                      className="hidden"
                      checked
                      value={ll.id}
                      onChange={(e) => handleChange(e.target.value)}
                    />
                  </label>
                ) : (
                  <label
                    key={ll.id}
                    htmlFor={`task-label${ll.id}`}
                    className="flex"
                  >
                    <div
                      data-tip={ll.name}
                      data-for={`label-${ll.id}`}
                      className="btn-checkbox-rounded"
                    >
                      <div className="w-32 truncate">{ll.name}</div>
                    </div>
                    <ReactTooltip
                      place="top"
                      type="dark"
                      effect="solid"
                      id={`label-${ll.id}`}
                      className="max-w-xs break-all text-xs"
                    />
                    <input
                      id={`task-label${ll.id}`}
                      type="checkbox"
                      className="hidden"
                      checked={false}
                      value={ll.id}
                      onChange={(e) => handleChange(e.target.value)}
                    />
                  </label>
                )
              )}
            </div>
            <div className="border border-primary mt-16 border-opacity-50" />
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button
                className="btn-sub"
                type="button"
                onClick={() => onSubmitted()}
              >
                キャンセル
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={disabled}
              >
                {disabled ? (
                  <>
                    <FontAwesomeIcon
                      icon={faSpinner}
                      className="animate-spin mr-4"
                    />
                    保 存
                  </>
                ) : (
                  '保　存'
                )}
              </button>
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
